import { useState, type ReactNode } from 'react';
import { useSession } from '../session';
import { priorityNames, stateNames, staffStateNames, statusNames } from '../i18n';
import type { TaskState } from '../api';

/** The colour each state carries on the board and in a badge. */
export const stateColour: Record<TaskState, string> = {
  new: 'var(--state-new)',
  in_progress: 'var(--state-progress)',
  waiting_client: 'var(--state-waiting)',
  in_stage: 'var(--state-stage)',
  closed: 'var(--state-closed)',
};

export const boardOrder: TaskState[] = [
  'new', 'in_progress', 'waiting_client', 'in_stage', 'closed',
];

/** A state's name depends on who is reading it: "in stage" is a studio word,
 *  and what the client is being told is that it waits for them. */
export function useStateName(): (state: string) => string {
  const { locale, user } = useSession();
  const table = user?.isStaff ? staffStateNames : stateNames;
  return (state) => table[locale][state] ?? state;
}

export function useStatusName(): (status: string) => string {
  const { locale } = useSession();
  return (status) => statusNames[locale][status] ?? status;
}

export function usePriorityName(): (priority: string) => string {
  const { locale } = useSession();
  return (priority) => priorityNames[locale][priority] ?? priority;
}

export function Avatar({ name }: { name: string | null }) {
  const initials = (name ?? '?')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]!)
    .filter((_, i, all) => i === 0 || i === all.length - 1)
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return <span className="avatar" title={name ?? undefined}>{initials}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Loading() {
  const { t } = useSession();
  return <p className="skeleton">{t.loading}…</p>;
}

/** Bodies are rich text: they came out of Drupal's editor and go back into
 *  one. The server sanitises on the way in, against the same allow-list the
 *  old format used, and is the only side that counts. */
export function Body({ html }: { html: string | null | undefined }) {
  if (!html) return null;
  return <div className="body" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function Hours({ used, total }: { used: number; total: number | null }) {
  const { t } = useSession();
  if (total === null || total === 0) {
    return <span className="muted">{hours(used)}h {t.hoursUsed}</span>;
  }
  const share = Math.min(used / total, 1);
  const over = used > total;
  // What is left, not what is gone: a package is bought to be spent, and the
  // question anybody asks of it is how much of it is still there.
  return (
    <>
      <span className={over ? '' : 'muted'}>
        {over
          ? `${hours(used - total)}h ${t.hoursOver} ${total}h`
          : `${hours(total - used)}h ${t.hoursLeft} ${t.hoursOf} ${total}h`}
      </span>
      <div className={over ? 'meter over' : 'meter'}>
        <span style={{ width: `${share * 100}%` }} />
      </div>
    </>
  );
}

export function when(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale === 'pt' ? 'pt-PT' : 'en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** Hours, always to the same two decimals.
 *
 *  Not rounded and not trimmed: 5.75 hours is five and three quarters, and
 *  showing it as 5.8 is a quarter of an hour that nobody agreed to. The
 *  columns line up for the same reason — a number that changes width between
 *  rows is harder to add up by eye. */
export function hoursOf(minutes: number): string {
  return hours(minutes / 60);
}

export function hours(value: number): string {
  return value.toFixed(2);
}

export type SortDirection = 'asc' | 'desc';

/** A column a table can be ordered by: what to compare, and which way it reads
 *  first. Dates and amounts start with the biggest, because "who was here last"
 *  and "what took longest" are the questions; words start at A. */
export type SortColumn<T> = {
  value: (row: T) => string | number | null | undefined;
  first?: SortDirection;
};

/** Ordering by a table's headers, in the browser. The first click on a column
 *  sorts by it, the next one turns it round. Until somebody clicks, the rows
 *  stay in the order the server sent them.
 *
 *  Empty values go to the bottom either way: somebody who has never signed in
 *  is not the most or the least recent, they are neither. */
export function useSort<T, K extends string>(
  rows: T[] | null | undefined,
  columns: Record<K, SortColumn<T>>,
) {
  const [sort, setSort] = useState<{ key: K; direction: SortDirection } | null>(null);
  const { locale } = useSession();

  // Not memoised: a column can read data that arrives after the click (the
  // projects behind a person's list of clients), and a few hundred rows sort
  // in less time than the render around them takes.
  const sorted = (() => {
    if (!rows || !sort) return rows ?? [];
    const { value } = columns[sort.key];
    const flip = sort.direction === 'asc' ? 1 : -1;
    const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
    return [...rows].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      const xEmpty = x === null || x === undefined || x === '';
      const yEmpty = y === null || y === undefined || y === '';
      if (xEmpty || yEmpty) return xEmpty === yEmpty ? 0 : xEmpty ? 1 : -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * flip;
      return collator.compare(String(x), String(y)) * flip;
    });
  })();

  const toggle = (key: K) => setSort((was) => (
    was?.key === key
      ? { key, direction: was.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: columns[key].first ?? 'asc' }
  ));

  /** The props a SortHeader needs for one column. */
  const header = (key: K) => ({
    direction: sort?.key === key ? sort.direction : null,
    onSort: () => toggle(key),
  });

  return { sorted, header };
}

/** A column heading that orders the table. The heading stays a heading — the
 *  button inside it is what a keyboard reaches and what aria-sort describes. */
export function SortHeader({
  children, direction, onSort, className,
}: {
  children: ReactNode;
  direction: SortDirection | null;
  onSort: () => void;
  className?: string;
}) {
  const numeric = className?.split(' ').includes('num') ?? false;
  const mark = (
    <span aria-hidden="true" className="sort-mark">
      {direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : ''}
    </span>
  );
  return (
    <th
      className={className}
      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : undefined}
    >
      {/* In a column of numbers the arrow goes on the left, so the label
          stays flush with the figures under it. */}
      <button type="button" className="sort" onClick={onSort}>
        {numeric && mark}
        {children}
        {!numeric && mark}
      </button>
    </th>
  );
}
